//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import {
  CreateCommunityTagRequest,
  CreateCommunityTagResponse,
  CreateInviteLinkRequest,
  CreateInviteLinkResponse,
  EditInviteLinkRequest,
  EditVibeCommunityApiRequest,
  EditVibeCommunityResponse,
  ExportCommunityMembersRequest,
  ExportInviteLinksRequest,
  InviteCommunityMembersCsvRequest,
  InviteCommunityMembersRequest,
  SearchCommunityMembersRequest,
  UploadVibeCommunityLogoRequest,
  VibeCommunitiesSearchResponse,
  VibeCommunity,
  VibeCommunityInviteLinkDetails,
  VibeCommunityInviteLinksRequest,
  VibeCommunityInviteLinksResponse,
  VibeCommunityLogoResponse,
  VibeCommunityMembersSearchResponse,
  VibeCommunityTagsResponse,
} from '@apis/campaign-requests/sampling/vibe-communities/vibeCommunitiesApiModel';
import VibeCommunityId from '@models/VibeCommunityId';
import { makeParams } from '@utils/makeParams';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

const VIBE_COMMUNITIES_ENDPOINT = '/api/v1/clients/vibe-communities';

const vibeCommunitiesFetch = (
  endpoint: string,
  init?: RequestInit,
  useFetchHeader?: boolean,
): Promise<ISerializedResponse> =>
  serializedGatewayFetch(
    'sampling_gateway',
    `${VIBE_COMMUNITIES_ENDPOINT}${endpoint}`,
    init,
    useFetchHeader,
  );

/**
 * Searches for Vibe Communities associated with the client's BV account.
 *
 * GET /api/v1/clients/vibe-communities/
 */
export const searchVibeCommunities = async (
  init?: RequestInit,
): Promise<VibeCommunitiesSearchResponse> => {
  const res = await vibeCommunitiesFetch('/', {
    ...init,
    method: 'GET',
  });

  const json = await res.json<{ data: VibeCommunitiesSearchResponse }>();
  return json.data;
};

/**
 * Retrieves detailed information about a specific Vibe Community.
 *
 * GET /api/v1/clients/vibe-communities/:communityId
 */
export const getVibeCommunity = async (
  req: { communityId: VibeCommunityId },
  init?: RequestInit,
): Promise<VibeCommunity> => {
  const res = await vibeCommunitiesFetch(`/${req.communityId}`, {
    ...init,
    method: 'GET',
  });

  const json = await res.json<{ data: VibeCommunity }>();
  return json.data;
};

/**
 * Returns tags associated with a specific Vibe Community.
 *
 * GET /api/v1/clients/vibe-communities/:communityId/tags
 */
export const getVibeCommunityTags = async (
  req: { communityId: VibeCommunityId },
  init?: RequestInit,
): Promise<VibeCommunityTagsResponse> => {
  const res = await vibeCommunitiesFetch(`/${req.communityId}/tags`, {
    ...init,
    method: 'GET',
  });

  const json = await res.json<{ data: VibeCommunityTagsResponse }>();
  return json.data;
};

/**
 * Creates a new tag for a specific Vibe Community.
 *
 * POST /api/v1/clients/vibe-communities/:communityId/tags
 */
export const createCommunityTag = async (
  req: CreateCommunityTagRequest,
  init?: RequestInit,
): Promise<CreateCommunityTagResponse> => {
  const { communityId, name } = req;

  const res = await vibeCommunitiesFetch(`/${communityId}/tags`, {
    ...init,
    body: JSON.stringify({ name }),
    method: 'POST',
  });

  const json = await res.json<{ data: CreateCommunityTagResponse }>();
  return json.data;
};

/**
 * Searches for members within a Vibe Community with filtering options.
 *
 * POST /api/v1/clients/vibe-communities/:communityId/members
 */
export const searchCommunityMembers = async (
  req: SearchCommunityMembersRequest,
  init?: RequestInit,
): Promise<VibeCommunityMembersSearchResponse> => {
  const { communityId, ...body } = req;

  const res = await vibeCommunitiesFetch(`/${communityId}/members`, {
    ...init,
    body: JSON.stringify(body),
    headers: {
      'Content-Type': 'application/json',
      ...init?.headers,
    },
    method: 'POST',
  });

  const json = await res.json<{ data: VibeCommunityMembersSearchResponse }>();
  return json.data;
};

/**
 * Exports community members as a CSV file.
 *
 * POST /api/v1/clients/vibe-communities/:communityId/members/export
 */
export const exportCommunityMembersCsv = async (
  req: ExportCommunityMembersRequest,
  init?: RequestInit,
): Promise<Blob> => {
  const { communityId, ...body } = req;

  const res = await vibeCommunitiesFetch(`/${communityId}/members/export`, {
    ...init,
    body: JSON.stringify(body),
    method: 'POST',
  });

  return res.blob();
};

/**
 * Uploads a logo file for a specific Vibe Community and returns the logo URL.
 *
 * POST /api/v1/clients/vibe-communities/:communityId/logo/upload
 */
export const uploadVibeCommunityLogo = async (
  req: UploadVibeCommunityLogoRequest,
  init?: RequestInit,
): Promise<VibeCommunityLogoResponse> => {
  const { communityId, file } = req;

  const formData = new FormData();
  formData.append('file', file, file.name);

  const res = await vibeCommunitiesFetch(
    `/${communityId}/logo/upload`,
    {
      ...init,
      body: formData,
      method: 'POST',
    },
    true,
  );

  const json: { data: VibeCommunityLogoResponse } = await res.json<{
    data: VibeCommunityLogoResponse;
  }>();
  return json.data;
};

/**
 * Updates a Vibe Community's name, description, and logo URL.
 *
 * PUT /api/v1/clients/vibe-communities/:communityId
 */
export const editVibeCommunity = async (
  req: EditVibeCommunityApiRequest,
  init?: RequestInit,
): Promise<EditVibeCommunityResponse> => {
  const { communityId, description, logoUrl, name } = req;

  const res = await vibeCommunitiesFetch(`/${communityId}`, {
    ...init,
    body: JSON.stringify({ name, description, logoUrl }),
    method: 'PUT',
  });

  const json = await res.json<{ data: EditVibeCommunityResponse }>();
  return json.data;
};

/**
 * Sends community invites to a list of email addresses.
 *
 * POST /api/v1/clients/vibe-communities/:communityId/members/invite
 */
export const inviteCommunityMembers = async (
  req: InviteCommunityMembersRequest,
  init?: RequestInit,
): Promise<void> => {
  const { communityId, emails, tagIds } = req;

  await vibeCommunitiesFetch(`/${communityId}/members/invite`, {
    ...init,
    body: JSON.stringify({ emails, tagIds }),
    method: 'POST',
  });
};

/**
 * Sends community invites from a CSV file containing email addresses.
 *
 * POST /api/v1/clients/vibe-communities/:communityId/members/invite
 */
export const inviteCommunityMembersCsv = async (
  req: InviteCommunityMembersCsvRequest,
  init?: RequestInit,
): Promise<void> => {
  const { communityId, file, tagIds } = req;

  const formData = new FormData();
  tagIds?.forEach((id) => formData.append('tagIds', id));
  formData.append('file', file, file.name);

  await vibeCommunitiesFetch(
    `/${communityId}/members/invite`,
    {
      ...init,
      body: formData,
      method: 'POST',
    },
    true,
  );
};

/**
 * Retrieves invite links for a Vibe Community.
 *
 * GET /api/v1/clients/vibe-communities/:communityId/invite-links
 */
export const getInviteLinks = async (
  req: VibeCommunityInviteLinksRequest,
  init?: RequestInit,
): Promise<VibeCommunityInviteLinksResponse> => {
  const { communityId, ...queryParams } = req;
  const params = makeParams(queryParams);

  const res = await vibeCommunitiesFetch(
    `/${communityId}/invite-links?${params}`,
    {
      ...init,
      method: 'GET',
    },
  );
  const json = await res.json<{ data: VibeCommunityInviteLinksResponse }>();
  return json.data;
};

/**
 * Creates invite links for a Vibe Community.
 *
 * POST /api/v1/clients/vibe-communities/:communityId/invite-links
 */
export const createInviteLinks = async (
  req: CreateInviteLinkRequest,
  init?: RequestInit,
): Promise<CreateInviteLinkResponse> => {
  const { communityId, ...body } = req;

  const res = await vibeCommunitiesFetch(`/${communityId}/invite-links`, {
    ...init,
    body: JSON.stringify(body),
    method: 'POST',
  });
  const json = await res.json<{ data: CreateInviteLinkResponse }>();
  return json.data;
};

/**
 * Updates an invite link for a Vibe Community.
 *
 * PATCH /api/v1/clients/vibe-communities/:communityId/invite-links/:inviteId
 */
export const editInviteLink = async (
  req: EditInviteLinkRequest,
  init?: RequestInit,
): Promise<VibeCommunityInviteLinkDetails> => {
  const { communityId, inviteId, ...body } = req;

  const res = await vibeCommunitiesFetch(
    `/${communityId}/invite-links/${inviteId}`,
    {
      ...init,
      body: JSON.stringify(body),
      method: 'PATCH',
    },
  );
  const json = await res.json<{ data: VibeCommunityInviteLinkDetails }>();
  return json.data;
};

/**
 * Exports invite links as a CSV file.
 *
 * GET /api/v1/clients/vibe-communities/:communityId/invite-links/export?inviteId=:inviteId
 */
export const exportInviteLinks = async (
  req: ExportInviteLinksRequest,
  init?: RequestInit,
): Promise<Blob> => {
  const { communityId, inviteId } = req;
  const params = makeParams({ inviteId });

  const res = await vibeCommunitiesFetch(
    `/${communityId}/invite-links/export?${params}`,
    {
      ...init,
      method: 'GET',
    },
  );

  return res.blob();
};
